import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LOGO } from "@/components/site/assets";

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [state, setState] = useState("verifying"); // verifying | invalid | form | done
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      setError("No activation token provided.");
      return;
    }
    api
      .get(`/activate/verify?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setInfo(data);
        setState("form");
      })
      .catch((err) => {
        setError(formatApiErrorDetail(err.response?.data?.detail));
        setState("invalid");
      });
    // token is the only real dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setSubmitting(true);
    try {
      await api.post("/activate", { token, password });
      setState("done");
      setTimeout(() => { window.location.href = "/platform"; }, 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="activate-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-10">
          <img src={LOGO} alt="JJ Speed Factory" className="h-14 w-auto object-contain mb-6" />
          <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-white/40">
            Athlete Platform
          </span>
        </div>

        {state === "verifying" && (
          <div className="flex flex-col items-center gap-3 text-white/40 py-10" data-testid="activate-verifying">
            <Loader2 className="animate-spin" size={28} />
            <p className="text-sm">Verifying your activation link...</p>
          </div>
        )}

        {state === "invalid" && (
          <div className="text-center py-6" data-testid="activate-invalid">
            <AlertCircle size={40} className="mx-auto text-red-400 mb-4" />
            <h1 className="font-display font-black uppercase tracking-tighter text-2xl mb-3">Link Unavailable</h1>
            <p className="text-white/50 text-sm mb-8">{error}</p>
            <a href="/" data-testid="activate-home" className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-white transition-colors">
              ← Back to Website
            </a>
          </div>
        )}

        {state === "form" && info && (
          <>
            <h1 className="font-display font-black uppercase tracking-tighter text-3xl text-center mb-2">
              Set Your Password
            </h1>
            <p className="text-center text-white/50 text-sm mb-8">
              Welcome, {info.name}. You're activating as a <span className="text-white font-bold">{info.role_label}</span>.
            </p>
            <form onSubmit={submit} className="space-y-5" data-testid="activate-form">
              <div>
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">Email</label>
                <input value={info.email} readOnly className="w-full bg-[#121212] border border-white/10 outline-none px-4 py-3 text-sm text-white/50" data-testid="activate-email" />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">New Password</label>
                <input data-testid="activate-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-[#121212] border border-white/10 focus:border-white outline-none px-4 py-3 text-sm transition-colors" />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-2">Confirm Password</label>
                <input data-testid="activate-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="w-full bg-[#121212] border border-white/10 focus:border-white outline-none px-4 py-3 text-sm transition-colors" />
              </div>
              {error && <p data-testid="activate-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">{error}</p>}
              <button data-testid="activate-submit" type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-sm py-3.5 hover:bg-white/85 transition-colors disabled:opacity-60">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />}
                {submitting ? "Activating..." : "Activate Account"}
              </button>
            </form>
          </>
        )}

        {state === "done" && (
          <div className="text-center py-8" data-testid="activate-done">
            <CheckCircle2 size={44} className="mx-auto text-white mb-4" />
            <h1 className="font-display font-black uppercase tracking-tighter text-2xl mb-3">You're In</h1>
            <p className="text-white/50 text-sm">Account activated. Taking you to your portal...</p>
          </div>
        )}
      </div>
    </div>
  );
}
